import React, { useEffect, useState } from 'react';
import { Button } from '@app/components/common/buttons/Button/Button';
import { UserApiPaginationModel, UserListModel } from '@app/models/UserModel';
import { ColumnsType } from 'antd/es/table';
import { Table, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@app/components/common/Table/Table';
import { PageTitle, Toolbar, ToolbarActions } from '@app/components/common/PageToolbar/PageToolbar';
import { useAppDispatch, useAppSelector } from '@app/hooks/reduxHooks';
import { doGetUsers, setAppVersions, setUsers } from '@app/store/slices/dataSlice';
import { notificationController } from '@app/controllers/notificationController';
import { Avatar, Card, Col, Divider, Input, Modal, Row, Space, Typography, Tag, Progress, Select, Dropdown, Checkbox, Badge } from 'antd';
import { EditOutlined, InfoOutlined, MailOutlined, WarningOutlined, ClockCircleOutlined, AlertOutlined, TagsOutlined, FireOutlined, CalendarOutlined, PhoneOutlined, UserOutlined, ReloadOutlined, DownloadOutlined, FilterOutlined, AppleOutlined, AndroidOutlined } from '@ant-design/icons';
import { DatePicker } from '@app/components/common/pickers/DatePicker';
import dayjs from 'dayjs';
import { Card as CommonCard } from '@app/components/common/Card/Card';
import { getModeDisplay, getProfilePhoto, parseUserExtraDetails, secondsToString } from '@app/utils/utils';
import { useNavigate } from 'react-router-dom';
import { getAppVersions, exportUsers } from '@app/api/admin.api';
import { ProfileInfo } from '@app/components/profile/profileCard/ProfileInfo/ProfileInfo';
import TroubleshootingDrawer from './TroubleshootingDrawer';
import { ToolOutlined } from '@ant-design/icons';
import { BaseChart } from '@app/components/common/charts/BaseChart';
import HealthDataSection from './HealthDataSection';
import ShopifyOrdersSection from './ShopifyOrdersSection';
import styled from 'styled-components';
import { AppVersions } from '@app/models/AppVersionModel';

const remapBattery = (raw: number) => Math.max(0, Math.round(((raw - 20) / (100 - 20)) * 100));

const formatMinutesToHoursMinutes = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

const formatDays = (days: number): string => {
  if (days < 30) return `${days} day${days !== 1 ? 's' : ''}`;
  const years = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30);
  const remainingDays = days % 30;

  const parts = [];
  if (years > 0) parts.push(`${years} year${years !== 1 ? 's' : ''}`);
  if (months > 0) parts.push(`${months} month${months !== 1 ? 's' : ''}`);
  if (remainingDays > 0) parts.push(`${remainingDays} day${remainingDays !== 1 ? 's' : ''}`);

  return parts.join(' ');
};

const extractBrand = (userExtraDetails: string | null): string | null => {
  try {
    const details = JSON.parse(userExtraDetails || '{}');
    return details.brand || null;
  } catch {
    return null;
  }
};

// Parses the raw C:ID:E response, e.g. "ID:(01)00860010877432(11)240201(10)2(21)E161600066"
const parseDeviceId = (raw: string | null) => {
  if (!raw) return null;
  const m = raw.match(/ID:\((\d+)\)(\d+)\(11\)(\d+)\(10\)(\d+)\(21\)([A-Za-z0-9]+)/);
  if (!m) return null;
  const [, prefix, id, mfg, batch, serial] = m;
  return { prefix, id, mfg, batch, serial };
};

// C:ID:E manufacturing date is YYMMDD (e.g. 240201 -> Feb 1, 2024)
const formatMfgDate = (yymmdd: string): string => {
  if (!yymmdd || yymmdd.length !== 6) return yymmdd || '—';
  const year = 2000 + parseInt(yymmdd.slice(0, 2), 10);
  const month = parseInt(yymmdd.slice(2, 4), 10);
  const day = parseInt(yymmdd.slice(4, 6), 10);
  const d = new Date(year, month - 1, day);
  return isNaN(d.getTime()) ? yymmdd : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getActivityLabel = (daysSinceSync: number | null): string => {
  if (daysSinceSync === null) return 'dormant';
  if (daysSinceSync <= 3) return 'active';
  if (daysSinceSync <= 30) return 'inactive';
  return 'dormant';
};

const getMostRecentCushion = (cushions: any[] | null): any | null => {
  if (!cushions || !Array.isArray(cushions) || cushions.length === 0) {
    console.log('No cushions array:', cushions);
    return null;
  }
  console.log('Cushions array:', cushions);
  // Rank by how recently the cushion was used, as a real timestamp. Prefer
  // last_sync_time (format YYYYMMDDHH, set once usage data syncs); fall back to
  // updated_at/created_at so a cushion that was just connected/registered — but
  // hasn't synced usage data yet — still outranks the user's previous cushion.
  const recencyTime = (c: any): number => {
    const ls = c?.last_sync_time;
    if (ls && /^\d{8,10}$/.test(ls)) {
      const year = +ls.slice(0, 4);
      const month = +ls.slice(4, 6) - 1;
      const day = +ls.slice(6, 8);
      const hour = +(ls.slice(8, 10) || '0');
      return new Date(year, month, day, hour).getTime();
    }
    const iso = c?.updated_at || c?.created_at;
    const t = iso ? new Date(iso).getTime() : NaN;
    return Number.isNaN(t) ? 0 : t;
  };
  const result = cushions.reduce((latest, current) =>
    recencyTime(current) >= recencyTime(latest) ? current : latest,
  );
  console.log('Most recent cushion:', result);
  return result;
};

const formatRelativeDate = (isoString: string | null): string => {
  if (!isoString) return '—';
  const date = new Date(isoString);
  const diffDays = Math.floor((Date.now() - date.getTime()) / 86400000);
  const abs = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (diffDays === 0) return `Today · ${abs}`;
  if (diffDays === 1) return `Yesterday · ${abs}`;
  return `${diffDays} days ago · ${abs}`;
};

const formatLastSync = (lastSessionDate: string | null): string => {
  if (!lastSessionDate) return '—';
  const [year, month, day] = lastSessionDate.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((today.getTime() - date.getTime()) / 86400000);
  const abs = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (diffDays === 0) return `Today · ${abs}`;
  if (diffDays === 1) return `Yesterday · ${abs}`;
  return `${diffDays} days ago · ${abs}`;
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, opacity: 0.5, marginBottom: 8 }}>{title}</div>
    {children}
  </div>
);

const InfoRow = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <>
    <Row>
      <Col span={12}><b>{label}</b></Col>
      <Col span={12} style={{ textAlign: 'right' }}>{value ?? '—'}</Col>
    </Row>
    <Divider style={{ margin: '10px 0', background: '#ffffff33' }} />
  </>
);

const compareVersions = (v1: string, v2: string): number => {
  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const part1 = parts1[i] || 0;
    const part2 = parts2[i] || 0;
    if (part1 > part2) return 1;
    if (part1 < part2) return -1;
  }
  return 0;
};

const buildChartOption = (breakdown: { date: string; minutes: number }[]) => {
  const labels: string[] = [];
  const tooltipLabels: string[] = [];
  const values: number[] = [];

  for (let i = 27; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const match = breakdown.find(b => b.date === key);

    const dayIndex = 27 - i;
    // Show date labels for every day, but use shorter format to save space
    const dateLabel = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    labels.push(dateLabel);
    tooltipLabels.push(d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }));
    values.push(match ? match.minutes : 0);
  }

  return {
    tooltip: {
      trigger: 'axis',
      formatter: (params: any) => `${tooltipLabels[params[0].dataIndex]}: ${formatMinutesToHoursMinutes(params[0].value)}`,
    },
    grid: { top: 8, bottom: 28, left: 4, right: 4 },
    xAxis: {
      type: 'category',
      data: labels,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { fontSize: 10, color: '#888' },
    },
    yAxis: { type: 'value', show: false },
    series: [{ type: 'bar', data: values, barMaxWidth: 10, itemStyle: { borderRadius: 3 } }],
  };
};

const UserInfoModal = ({ userData, appVersions, onClose, onRefresh, refreshing, refreshNonce }: { userData: UserListModel, appVersions: AppVersions, onClose: any, onRefresh: () => void, refreshing: boolean, refreshNonce: number }) => {
  const [troubleshootOpen, setTroubleshootOpen] = useState(false);
  if (!userData) { return null }
  const user = parseUserExtraDetails(userData.user);
  const brand = extractBrand(userData.user.user_extra_details);

  // Get most recent cushion from cushions array (access directly from userData.user)
  const mostRecentCushion = getMostRecentCushion(userData.user?.cushions);

  const isiOS = user?.platform?.includes("iOS") || user?.platform?.includes("iPhone OS") || user?.platform?.includes("iPadOS");
  const isAndroid = !!user?.platform?.toLowerCase().includes("android");
  const platformLabel = isiOS ? 'iOS' : isAndroid ? 'Android' : null;
  const latestAppVersion = isiOS ? appVersions?.ios?.trim() || "" : appVersions?.android?.trim() || "";
  const userAppVersion = user?.app_version?.split(" ")?.[0]?.trim() || "";
  const isAppLatestVersion = latestAppVersion && userAppVersion ? compareVersions(userAppVersion, latestAppVersion) >= 0 : false;

  // Determine status (updated thresholds: 0-3 Active, 4-30 Inactive, 30+ Dormant)
  let statusColor = 'red';
  let statusLabel = 'Dormant';
  if (userData.days_since_sync !== null && userData.days_since_sync <= 3) {
    statusColor = 'green';
    statusLabel = 'Active';
  } else if (userData.days_since_sync !== null && userData.days_since_sync >= 4 && userData.days_since_sync <= 30) {
    statusColor = 'orange';
    statusLabel = 'Inactive';
  }

  // Collect alerts
  const alerts: Array<{ label: string; icon: React.ReactNode }> = [];
  if (!isAppLatestVersion && latestAppVersion) {
    alerts.push({ label: 'App is outdated', icon: <WarningOutlined /> });
  }
  if (userData.days_since_sync !== null && userData.days_since_sync > 30) {
    alerts.push({ label: 'No sync in 30+ days', icon: <WarningOutlined /> });
  }
  // Only show "No cushion paired" if no cushion is assigned
  if (!mostRecentCushion && userData.time_spent === 0) {
    alerts.push({ label: 'No cushion paired', icon: <WarningOutlined /> });
  }

  // Battery color logic
  const batteryPercent = userData.last_battery !== null ? remapBattery(userData.last_battery) : null;
  let batteryColor = '#1890ff';
  if (batteryPercent !== null) {
    if (batteryPercent > 50) batteryColor = '#52c41a';
    else if (batteryPercent >= 20) batteryColor = '#faad14';
    else batteryColor = '#f5222d';
  }

  // Calculate daily average
  const dailyAverage = userData.account_age_days > 0
    ? Math.round(userData.time_spent / userData.account_age_days)
    : 0;

  return <Modal
    visible={user !== null}
    onCancel={() => onClose()}
    footer={null}
    width="90vw"
    style={{ maxWidth: '1400px' }}
  >
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Toolbar: troubleshooting + refresh this user's data without closing the popup */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginRight: 28, gap: 8 }}>
        <Button type="default" size="small" icon={<ToolOutlined />} onClick={() => setTroubleshootOpen(true)}>
          Troubleshooting
        </Button>
        <Button type="default" size="small" icon={<ReloadOutlined />} loading={refreshing} onClick={onRefresh}>
          Refresh
        </Button>
      </div>
      <TroubleshootingDrawer
        userId={userData.user.id}
        userName={userData.user.name || userData.user.email}
        open={troubleshootOpen}
        onClose={() => setTroubleshootOpen(false)}
      />

      {/* Lost/stolen cushion detected for this person. Put at the top of the
          popup because it changes how support should handle the whole
          conversation. */}
      {(userData.flagged_hits || []).length > 0 && (
        <div
          style={{
            padding: 12,
            borderRadius: 8,
            border: '1px solid #ff3d71',
            background: 'rgba(255, 61, 113, 0.10)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <WarningOutlined style={{ color: '#ff3d71' }} />
            <b>Lost / stolen cushion connected</b>
          </div>
          {(userData.flagged_hits || []).map((f) => (
            <div key={f.serial} style={{ fontSize: 13 }}>
              <Typography.Text code>{f.serial}</Typography.Text>{' '}
              <Tag color={f.reason === 'stolen' ? 'red' : 'orange'}>{f.reasonLabel}</Tag>
              {f.alsoHasReplacement && (
                <Tag color="red">Also has the replacement — using both</Tag>
              )}
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                detected {new Date(f.detectedAt).toLocaleDateString()}
              </Typography.Text>
            </div>
          ))}
        </div>
      )}

      {/* Header: Profile Info Left + Key Metrics Right (70/30 split) */}
      <Row gutter={[24, 16]} align="top">
        {/* Left: Profile (smaller width) */}
        <Col xs={24} md={7}>
          <div style={{ textAlign: 'center' }}>
            <ProfileInfo profileData={user} />
          </div>
        </Col>

        {/* Right: Key Metrics + Alerts + Brand in 2-column grid (larger width) */}
        <Col xs={24} md={17}>
          <Row gutter={[12, 12]}>
            {/* Row 1: Status (with legend) + Alerts */}
            <Col xs={12}>
              <div style={{ padding: 12, background: 'var(--soft-panel-bg)', borderRadius: 8, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 80, gap: 6, border: 'var(--soft-panel-border)', boxShadow: 'var(--soft-panel-shadow)' }}>
                <Tag color={statusColor} style={{ fontSize: 12, fontWeight: 600 }}>{statusColor === 'green' ? '🟢' : statusColor === 'orange' ? '🟡' : '🔴'} {statusLabel}</Tag>
              </div>
            </Col>
            <Col xs={12}>
              <div style={{ padding: 12, background: 'var(--soft-panel-bg)', borderRadius: 8, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 80, border: 'var(--soft-panel-border)', boxShadow: 'var(--soft-panel-shadow)' }}>
                <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 8, textAlign: 'center' }}>Alerts</div>
                {alerts.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
                    {alerts.map((alert, i) => (
                      <Tag color="warning" key={i} icon={alert.icon} style={{ fontSize: 11, width: 'fit-content' }}>{alert.label}</Tag>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, opacity: 0.7, fontWeight: 500 }}>✓ All Clear</div>
                )}
              </div>
            </Col>

            {/* Row 2: Last Cushion Sync + Battery */}
            <Col xs={12}>
              <div style={{ padding: 12, background: 'var(--soft-panel-bg)', borderRadius: 8, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 80, border: 'var(--soft-panel-border)', boxShadow: 'var(--soft-panel-shadow)' }}>
                <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 4, textAlign: 'center' }}>Last Sync</div>
                <div style={{ fontSize: 13, fontWeight: 600, textAlign: 'center' }}>
                  {mostRecentCushion?.last_sync_time ? (() => {
                    const dateStr = mostRecentCushion.last_sync_time.slice(0, 8);
                    const formatted = dateStr.slice(0, 4) + '-' + dateStr.slice(4, 6) + '-' + dateStr.slice(6, 8);
                    return formatLastSync(formatted);
                  })() : formatLastSync(userData.last_session_date)}
                </div>
              </div>
            </Col>
            <Col xs={12}>
              <div style={{ padding: 12, background: 'var(--soft-panel-bg)', borderRadius: 8, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 80, border: 'var(--soft-panel-border)', boxShadow: 'var(--soft-panel-shadow)' }}>
                <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 8, textAlign: 'center' }}>Battery</div>
                {batteryPercent !== null ? (
                  <Progress type="circle" percent={batteryPercent} width={40} strokeColor={batteryColor} format={(percent) => `${percent}%`} />
                ) : (
                  <div style={{ fontSize: 13 }}>—</div>
                )}
              </div>
            </Col>

            {/* Row 3: Member For + Brand */}
            <Col xs={12}>
              <div style={{ padding: 12, background: 'var(--soft-panel-bg)', borderRadius: 8, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 80, border: 'var(--soft-panel-border)', boxShadow: 'var(--soft-panel-shadow)' }}>
                <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 4, textAlign: 'center' }}>Member Since</div>
                <div style={{ fontSize: 14, fontWeight: 600, textAlign: 'center' }}>{formatDays(userData.account_age_days)}</div>
              </div>
            </Col>

            {/* Row 4: Brand + Platform Tags */}
            {(brand || platformLabel) && (
              <Col xs={12}>
                <div style={{ padding: 12, background: 'var(--soft-panel-bg)', borderRadius: 8, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 80, border: 'var(--soft-panel-border)', boxShadow: 'var(--soft-panel-shadow)' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {brand && (
                      <Tag color="default" style={{
                        backgroundColor: brand === 'ease' ? '#4A7556' : brand === 'wavon' ? '#D89070' : '#ccc',
                        color: 'white',
                        border: 'none',
                        fontSize: 13,
                        fontWeight: 600,
                        padding: '4px 12px',
                        margin: 0
                      }}>
                        {brand.charAt(0).toUpperCase() + brand.slice(1)}
                      </Tag>
                    )}
                    {platformLabel && (
                      <Tag
                        icon={isiOS ? <AppleOutlined /> : <AndroidOutlined />}
                        style={{
                          backgroundColor: isiOS ? '#555' : '#3DDC84',
                          color: 'white',
                          border: 'none',
                          fontSize: 13,
                          fontWeight: 600,
                          padding: '4px 12px',
                          margin: 0
                        }}
                      >
                        {platformLabel}
                      </Tag>
                    )}
                  </div>
                </div>
              </Col>
            )}
          </Row>
        </Col>
      </Row>

      {/* Chart - Full Width */}
      <div style={{ padding: 12, background: 'var(--soft-panel-bg)', borderRadius: 8, border: 'var(--soft-panel-border)', boxShadow: 'var(--soft-panel-shadow)' }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <FireOutlined style={{ color: '#ff7a45' }} />
          Usage — Last 4 Weeks
        </div>
        <BaseChart option={buildChartOption(userData.usage_breakdown)} height="200px" />
      </div>

      {/* Apple Health — lazy-loaded on expand; sits with the usage chart as the
          other data-viz section */}
      <HealthDataSection userId={userData.user.id} usageBreakdown={userData.usage_breakdown} refreshNonce={refreshNonce} />

      {/* Shopify orders — what this person actually bought, with their cushion
          serial. Lazy-loaded on expand, same as the health section. */}
      <ShopifyOrdersSection userId={userData.user.id} refreshNonce={refreshNonce} />

      {/* Three Column Layout */}
      <Row gutter={[16, 16]}>
        {/* Left: Usage Details */}
        <Col xs={24} md={8}>
          <div style={{ padding: 12, background: 'var(--soft-panel-bg)', borderRadius: 8, border: 'var(--soft-panel-border)', boxShadow: 'var(--soft-panel-shadow)' }}>
            <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.6, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <FireOutlined style={{ color: '#ff7a45' }} />
              Usage & Activity
            </div>
            <InfoRow label="Total Time" value={userData.time_spent > 0 ? secondsToString(userData.time_spent) : 'No data'} />
            <InfoRow label="Last 7 Days" value={userData.seven_day_time_spent > 0 ? secondsToString(userData.seven_day_time_spent) : 'No data'} />
            <InfoRow label="Daily Average" value={dailyAverage > 0 ? secondsToString(dailyAverage) : 'No data'} />
            <InfoRow label="Last Login" value={formatRelativeDate(userData.last_login)} />
            <InfoRow label="Account Created" value={formatRelativeDate(userData.date_joined)} />
          </div>
        </Col>

        {/* Center: Device & App */}
        <Col xs={24} md={8}>
          <div style={{ padding: 12, background: 'var(--soft-panel-bg)', borderRadius: 8, border: 'var(--soft-panel-border)', boxShadow: 'var(--soft-panel-shadow)' }}>
            <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.6, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ fontSize: 20, color: '#52c41a' }}>⚙️</div>
              Device
            </div>
            <InfoRow label="Cushion" value={mostRecentCushion?.name || 'Not paired'} />
            <InfoRow label="Firmware" value={mostRecentCushion?.firmware_number || user.firmware_number} />
            <InfoRow label="App Version" value={<span style={{ color: !latestAppVersion ? 'white' : isAppLatestVersion ? '#52c41a' : '#f5222d' }}>{user.app_version}</span>} />
            <InfoRow label="Platform" value={user.platform} />
            {(() => {
              const raw = mostRecentCushion?.device_id_response || null;
              const did = parseDeviceId(raw);
              if (!did) {
                return <InfoRow label="Device ID" value={raw || '—'} />;
              }
              return (
                <>
                  <InfoRow label="Device ID" value={did.id} />
                  <InfoRow label="Manufactured" value={formatMfgDate(did.mfg)} />
                  <InfoRow label="Batch" value={did.batch} />
                  <InfoRow label="Serial" value={did.serial} />
                  <div style={{ fontSize: 10, opacity: 0.5, wordBreak: 'break-all', marginTop: -2 }}>{raw}</div>
                </>
              );
            })()}
          </div>
        </Col>

        {/* Right: Personal & Therapeutic */}
        <Col xs={24} md={8}>
          <div style={{ padding: 12, background: 'var(--soft-panel-bg)', borderRadius: 8, border: 'var(--soft-panel-border)', boxShadow: 'var(--soft-panel-shadow)' }}>
            <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.6, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <UserOutlined style={{ color: '#1890ff' }} />
              Profile
            </div>
            <InfoRow label="Age" value={user.age ? `${user.age} years${user.is_dob_approximate ? ' (approx.)' : ''}` : '—'} />
            <InfoRow label="Date of Birth" value={user.date_of_birth || '—'} />
            <InfoRow label="Height" value={user.height} />
            <InfoRow label="Weight" value={user.weight ? `${user.weight} lbs` : '—'} />
            <InfoRow label="Gender" value={user.gender === 0 ? 'Male' : user.gender === 1 ? 'Female' : 'Other'} />
            <InfoRow label="Phone" value={user.phone_number || '—'} />
            <Row style={{ marginBottom: 8 }}>
              <Col span={12}><b>Symptoms</b></Col>
              <Col span={12} style={{ textAlign: 'right' }}>{user.symptoms || 'Not specified'}</Col>
            </Row>
            <Divider style={{ margin: '10px 0', background: '#ffffff33' }} />
            <Row>
              <Col span={12}><b>Mode</b></Col>
              <Col span={12} style={{ textAlign: 'right' }}>{getModeDisplay(userData.user_mode)}</Col>
            </Row>
            <Divider style={{ margin: '10px 0', background: '#ffffff33' }} />
          </div>
        </Col>
      </Row>

    </div>
  </Modal>
}

// ---- Consolidated Users filters ----------------------------------------
// Checkbox groups (OR within a group); batch/firmware/app-version are free-text
// (comma-separate for multiple). All sent server-side so they filter every page.
type UserFilters = {
  activity: string[];
  brand: string[];
  device: string[];
  platform: string[];
  gender: string[];
  shopify: string[];
  flagged: boolean;
  batch: string;
  firmware: string;
  app_version: string;
  staff: boolean;
  joinedFrom: string | null;
  joinedTo: string | null;
};

const EMPTY_FILTERS: UserFilters = {
  activity: [], brand: [], device: [], platform: [], gender: [], shopify: [], flagged: false,
  batch: '', firmware: '', app_version: '',
  staff: false, joinedFrom: null, joinedTo: null,
};

const buildFilterParams = (f: UserFilters): Partial<UserApiPaginationModel> => ({
  activity: f.activity.length ? f.activity.join(',') : undefined,
  brand: f.brand.length ? f.brand.join(',') : undefined,
  device: f.device.length ? f.device.join(',') : undefined,
  platform: f.platform.length ? f.platform.join(',') : undefined,
  gender: f.gender.length ? f.gender.join(',') : undefined,
  shopify: f.shopify.length ? f.shopify.join(',') : undefined,
  flagged: f.flagged ? 'true' : undefined,
  batch: f.batch.trim() || undefined,
  firmware: f.firmware.trim() || undefined,
  app_version: f.app_version.trim() || undefined,
  staff: f.staff ? 'true' : undefined,
  joined_from: f.joinedFrom || undefined,
  joined_to: f.joinedTo || undefined,
});

const countActiveFilters = (f: UserFilters): number =>
  (f.activity.length ? 1 : 0) + (f.brand.length ? 1 : 0) + (f.device.length ? 1 : 0) +
  (f.platform.length ? 1 : 0) + (f.gender.length ? 1 : 0) + (f.shopify.length ? 1 : 0) + (f.flagged ? 1 : 0) +
  (f.batch.trim() ? 1 : 0) + (f.firmware.trim() ? 1 : 0) + (f.app_version.trim() ? 1 : 0) +
  (f.staff ? 1 : 0) + (f.joinedFrom || f.joinedTo ? 1 : 0);

// Human-readable [label, value] pairs for the applied filters (export modal).
const describeFilters = (f: UserFilters): [string, string][] => {
  const rows: [string, string][] = [];
  const list = (label: string, arr: string[]) => { if (arr.length) rows.push([label, arr.join(', ')]); };
  list('Activity', f.activity);
  list('Brand', f.brand);
  list('Device', f.device);
  list('Platform', f.platform);
  list('Gender', f.gender.map((g) => ({ '0': 'Male', '1': 'Female', '2': 'Other' }[g] || g)));
  list('Shopify', f.shopify);
  if (f.flagged) rows.push(['Flagged', 'Lost/stolen only']);
  if (f.staff) rows.push(['Staff', 'Staff only']);
  if (f.batch.trim()) rows.push(['Batch', f.batch.trim()]);
  if (f.firmware.trim()) rows.push(['Firmware', f.firmware.trim()]);
  if (f.app_version.trim()) rows.push(['App version', f.app_version.trim()]);
  if (f.joinedFrom || f.joinedTo) rows.push(['Joined', `${f.joinedFrom || '…'} → ${f.joinedTo || '…'}`]);
  return rows;
};

const UsersPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const users = useAppSelector((state) => state.data.users);
  const appVersions = useAppSelector((state) => state.data.appVersions);

  const [isLoading, setLoading] = useState(false);

  const [keyWord, setKeyWord] = useState('')
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [userForModal, setUserForModal] = useState<UserListModel>(null);
  const [modalRefreshing, setModalRefreshing] = useState(false);
  // Bumped on each refresh so lazy-loaded sections (Health, Shopify) re-fetch
  // even when currently collapsed but already opened once.
  const [modalRefreshNonce, setModalRefreshNonce] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [appliedFilters, setAppliedFilters] = useState<UserFilters>(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState<UserFilters>(EMPTY_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);

  // Export-to-Excel modal state; the export uses the filters applied to the table.
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    getAppVersions().then(data => {
      dispatch(setAppVersions({ ios: data.ios, android: data.android }))
    }).catch(err => {
      console.error('Failed to fetch app versions:', err);
    })
  }, [])

  useEffect(() => {
    const DEBOUNCE_DELAY = 300;
    const handler = setTimeout(() => {
      fetchUsers({ page: 1, page_size: pageSize, search: keyWord, ...buildFilterParams(appliedFilters) });
      setCurrentPage(1);
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(handler);
  }, [keyWord]);

  const fetchUsers = (params: UserApiPaginationModel) => {
    setLoading(true);
    dispatch(doGetUsers(params))
      .unwrap()
      .then((res) => {
        dispatch(setUsers(res));
        setLoading(false);
      })
      .catch((err) => {
        notificationController.error({ message: err.message });
        setLoading(false);
      });
  };

  // Refresh the currently open user's data without closing the popup.
  // Re-uses the existing list endpoint scoped to the user's email (which
  // returns the full UserListModel incl. usage metrics) and swaps in the
  // fresh record; also patches the matching row in the visible list.
  const refreshModalUser = () => {
    if (!userForModal) return;
    const { id, email } = userForModal.user;
    setModalRefreshing(true);
    setModalRefreshNonce((n) => n + 1); // trigger re-fetch of expanded sub-sections
    dispatch(doGetUsers({ page: 1, search: email }))
      .unwrap()
      .then((res) => {
        const fresh = res.results?.find((r) => r.user.id === id);
        if (!fresh) {
          notificationController.info({ message: 'No updated data found for this user.' });
          return;
        }
        setUserForModal(fresh);
        if (users.results?.some((r) => r.user.id === id)) {
          dispatch(setUsers({ ...users, results: users.results.map((r) => (r.user.id === id ? fresh : r)) }));
        }
        notificationController.success({ message: 'User data refreshed.' });
      })
      .catch((err) => {
        notificationController.error({ message: err.message || err });
      })
      .finally(() => {
        setModalRefreshing(false);
      });
  };

  const columns: ColumnsType<UserListModel> = [
    {
      title: 'User',
      dataIndex: 'name',
      render: (text: string, record: UserListModel) => {
        const { user } = record;
        const flags = record.flagged_hits || [];
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar src={getProfilePhoto(user.profile_picture)} size={36} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>{user.name || '—'}</span>
                {flags.length > 0 && (
                  <Tag
                    color={flags.some((f) => f.reason === 'stolen') ? 'red' : 'orange'}
                    style={{ margin: 0 }}
                    title={flags
                      .map((f) => `${f.serial} — ${f.reasonLabel}${f.alsoHasReplacement ? ' (has both units)' : ''}`)
                      .join('\n')}
                  >
                    {flags.some((f) => f.reason === 'stolen') ? 'Stolen unit' : 'Lost unit'}
                  </Tag>
                )}
              </div>
              <div style={{ fontSize: 12, opacity: 0.6 }}>{user.email}</div>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Brand',
      dataIndex: ['user', 'user_extra_details'],
      width: 90,
      render: (userExtraDetails: string | null) => {
        const brand = extractBrand(userExtraDetails);
        if (!brand) return <span>—</span>;
        const colorMap = { ease: '#4A7556', wavon: '#D89070' };
        const color = colorMap[brand.toLowerCase()] || '#ccc';
        return (
          <Tag style={{ backgroundColor: color, color: '#fff', border: 'none' }}>
            {brand.charAt(0).toUpperCase() + brand.slice(1)}
          </Tag>
        );
      },
    },
    {
      title: 'Activity',
      dataIndex: 'days_since_sync',
      width: 110,
      render: (daysSinceSync: number | null) => {
        const activity = getActivityLabel(daysSinceSync);
        const colorMap = { active: 'green', inactive: 'orange', dormant: 'red' };
        const labelMap = { active: '🟢 Active', inactive: '🟡 Inactive', dormant: '🔴 Dormant' };
        return <Tag color={colorMap[activity]}>{labelMap[activity]}</Tag>;
      },
    },
    {
      title: 'Account',
      dataIndex: ['user', 'is_active'],
      width: 100,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Enabled' : 'Disabled'}
        </Tag>
      ),
    },
    {
      title: 'Last Sync',
      dataIndex: 'last_session_date',
      width: 130,
      render: (lastSessionDate: string | null) => formatLastSync(lastSessionDate),
    },
    {
      title: 'Usage',
      dataIndex: 'time_spent',
      width: 140,
      render: (_, { time_spent, seven_day_time_spent }: UserListModel) => (
        <div>
          <div style={{ fontWeight: 600 }}>
            {formatMinutesToHoursMinutes(seven_day_time_spent / 60)}
            <span style={{ fontWeight: 400, opacity: 0.6, fontSize: 11 }}> (7d)</span>
          </div>
          <div style={{ fontSize: 11, opacity: 0.5 }}>{formatMinutesToHoursMinutes(time_spent / 60)} total</div>
        </div>
      ),
    },
    {
      title: 'Device',
      dataIndex: ['user', 'cushions'],
      width: 130,
      render: (cushions: any) => {
        const cushion = getMostRecentCushion(cushions);
        return cushion ? <span>{cushion.name}</span> : <span style={{ opacity: 0.4 }}>Not paired</span>;
      },
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      width: 110,
      render: (text: string, record: UserListModel) => {
        return (
          <Space>
            <Button size="small" type="primary" icon={<InfoOutlined />} onClick={() => setUserForModal(record)} />
            <Button size="small" type="primary" icon={<EditOutlined />} onClick={() => navigate(`edit`, { state: { record: record.user } })} />
            <Button size="small" type="primary" icon={<MailOutlined />} onClick={() => navigate(`notify`, { state: { record: record.user } })} />
          </Space>
        );
      },
    },
  ];

  const openExportModal = () => setExportModalOpen(true);

  const handleExport = () => {
    setExporting(true);
    exportUsers({ search: keyWord || undefined, ...buildFilterParams(appliedFilters) })
      .then(({ blob, filename }) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        notificationController.success({ message: 'Export downloaded.' });
        setExportModalOpen(false);
      })
      .catch((err) => {
        notificationController.error({ message: err.message || 'Export failed.' });
      })
      .finally(() => setExporting(false));
  };

  const onFilterOpenChange = (open: boolean) => {
    if (open) setDraftFilters(appliedFilters); // start editing from the applied set
    setFilterOpen(open);
  };

  const applyFilters = () => {
    setAppliedFilters(draftFilters);
    setCurrentPage(1);
    fetchUsers({ page: 1, page_size: pageSize, search: keyWord, ...buildFilterParams(draftFilters) });
    setFilterOpen(false);
  };

  const clearDraftFilters = () => setDraftFilters(EMPTY_FILTERS);

  const setDraft = (patch: Partial<UserFilters>) => setDraftFilters((d) => ({ ...d, ...patch }));

  const appliedCount = countActiveFilters(appliedFilters);
  const exportFilterSummary = describeFilters(appliedFilters);

  const filterPanel = (
    <div onClick={(e) => e.stopPropagation()}>
    <Card
      size="small"
      style={{ width: 600, maxWidth: '92vw', boxShadow: '0 6px 24px rgba(0,0,0,0.18)' }}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 28px' }}>
        <div style={{ minWidth: 160 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Activity</div>
          <Checkbox.Group
            value={draftFilters.activity}
            onChange={(v) => setDraft({ activity: v as string[] })}
            options={[
              { label: '🟢 Active', value: 'active' },
              { label: '🟡 Inactive', value: 'inactive' },
              { label: '🔴 Dormant', value: 'dormant' },
            ]}
            style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
          />
        </div>
        <div style={{ minWidth: 120 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Brand</div>
          <Checkbox.Group
            value={draftFilters.brand}
            onChange={(v) => setDraft({ brand: v as string[] })}
            options={[{ label: 'Ease', value: 'ease' }, { label: 'Wavon', value: 'wavon' }]}
            style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
          />
        </div>
        <div style={{ minWidth: 150 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Device</div>
          <Checkbox.Group
            value={draftFilters.device}
            onChange={(v) => setDraft({ device: v as string[] })}
            options={[{ label: 'Cushion paired', value: 'paired' }, { label: 'No cushion', value: 'unpaired' }]}
            style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
          />
        </div>
        <div style={{ minWidth: 120 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Platform</div>
          <Checkbox.Group
            value={draftFilters.platform}
            onChange={(v) => setDraft({ platform: v as string[] })}
            options={[{ label: 'iOS', value: 'ios' }, { label: 'Android', value: 'android' }]}
            style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
          />
        </div>
        <div style={{ minWidth: 120 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Gender</div>
          <Checkbox.Group
            value={draftFilters.gender}
            onChange={(v) => setDraft({ gender: v as string[] })}
            options={[
              { label: 'Male', value: '0' },
              { label: 'Female', value: '1' },
              { label: 'Other', value: '2' },
            ]}
            style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
          />
        </div>
        <div style={{ minWidth: 170 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Shopify order</div>
          {/* "Not linked" is the one support asked for: app users we have never
              connected to a purchase. The match types narrow that further --
              serial is hardware-verified, email is only a guess. */}
          <Checkbox.Group
            value={draftFilters.shopify}
            onChange={(v) => setDraft({ shopify: v as string[] })}
            options={[
              { label: 'Linked', value: 'linked' },
              { label: 'Not linked', value: 'unlinked' },
              { label: 'By serial', value: 'serial' },
              { label: 'By email', value: 'email' },
              { label: 'Linked manually', value: 'manual' },
            ]}
            style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
          />
        </div>
        <div style={{ minWidth: 150 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Other</div>
          <Checkbox
            checked={draftFilters.staff}
            onChange={(e) => setDraft({ staff: e.target.checked })}
          >
            Staff / Admin only
          </Checkbox>
          <Checkbox
            checked={draftFilters.flagged}
            onChange={(e) => setDraft({ flagged: e.target.checked })}
          >
            Lost / stolen cushion
          </Checkbox>
        </div>
      </div>

      <Divider style={{ margin: '14px 0' }} />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 24px' }}>
        <div style={{ minWidth: 170 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Batch</div>
          <Input
            value={draftFilters.batch}
            onChange={(e) => setDraft({ batch: e.target.value })}
            title="Matches a paired cushion or a linked Shopify order's serial"
            placeholder="e.g. 2 or 2,3"
            allowClear
          />
        </div>
        <div style={{ minWidth: 200 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Firmware</div>
          <Input
            value={draftFilters.firmware}
            onChange={(e) => setDraft({ firmware: e.target.value })}
            placeholder="e.g. 3.5.1 or 3.5.1,3.6.0"
            allowClear
          />
        </div>
        <div style={{ minWidth: 170 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>App version</div>
          <Input
            value={draftFilters.app_version}
            onChange={(e) => setDraft({ app_version: e.target.value })}
            placeholder="e.g. 1.4.2"
            allowClear
          />
        </div>
        <div style={{ minWidth: 260 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Joined between</div>
          <Space>
            <DatePicker
              value={draftFilters.joinedFrom ? dayjs(draftFilters.joinedFrom) : null}
              onChange={(d: any) => setDraft({ joinedFrom: d ? d.format('YYYY-MM-DD') : null })}
              placeholder="From"
              getPopupContainer={(t) => t.parentElement as HTMLElement}
            />
            <DatePicker
              value={draftFilters.joinedTo ? dayjs(draftFilters.joinedTo) : null}
              onChange={(d: any) => setDraft({ joinedTo: d ? d.format('YYYY-MM-DD') : null })}
              placeholder="To"
              getPopupContainer={(t) => t.parentElement as HTMLElement}
            />
          </Space>
        </div>
      </div>

      <Divider style={{ margin: '14px 0' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button type="ghost" onClick={clearDraftFilters}>Clear all</Button>
        <Button type="primary" onClick={applyFilters}>Apply</Button>
      </div>
    </Card>
    </div>
  );

  return (
    <>
      <Toolbar>
        <PageTitle>Users</PageTitle>
        <ToolbarActions>
        <Input
          style={{ width: 340 }}
          placeholder="Search name, email, phone, serial…"
          value={keyWord}
          onChange={e => setKeyWord(e.target.value)}
          allowClear
        />
        <Dropdown
          visible={filterOpen}
          onVisibleChange={onFilterOpenChange}
          trigger={['click']}
          placement="bottomRight"
          overlay={filterPanel}
        >
          <Badge count={appliedCount} size="small">
            <Button icon={<FilterOutlined />}>Filters</Button>
          </Badge>
        </Dropdown>
        <Button type="primary" icon={<DownloadOutlined />} onClick={openExportModal}>Export to Excel</Button>
        </ToolbarActions>
      </Toolbar>
      <div style={{ fontSize: 12, opacity: 0.5, marginBottom: 8 }}>
        Showing {users.results?.length ?? 0} of {users.count ?? 0} users (page {currentPage})
      </div>
      <CommonCard id="basic-table" padding="1.25rem 1.25rem 0">
        <Table
          columns={columns}
          dataSource={users.results}
          loading={isLoading}
          pagination={{
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            current: currentPage,
            pageSize,
            total: users.count,
            // Server-side paging: a size change has to refetch, not re-slice
            // the ten rows already on screen.
            onChange: (page, size) => {
              const nextSize = size || pageSize;
              setCurrentPage(page);
              setPageSize(nextSize);
              fetchUsers({
                page, page_size: nextSize, search: keyWord, ...buildFilterParams(appliedFilters),
              });
            },
          }}
          scroll={{ x: 800 }} />
      </CommonCard>
      <UserInfoModal userData={userForModal} appVersions={appVersions} onClose={() => setUserForModal(null)} onRefresh={refreshModalUser} refreshing={modalRefreshing} refreshNonce={modalRefreshNonce} />
      <Modal
        title="Export Users to Excel"
        visible={exportModalOpen}
        onCancel={() => setExportModalOpen(false)}
        onOk={handleExport}
        okText="Download"
        confirmLoading={exporting}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            Exports all users matching the current table filters
            {keyWord ? ` and search “${keyWord}”` : ''} — not just the current page.
          </Typography.Text>
          {exportFilterSummary.length ? (
            <div>
              {exportFilterSummary.map(([label, value]) => (
                <div key={label}><Typography.Text strong>{label}:</Typography.Text> {value}</div>
              ))}
            </div>
          ) : (
            <Typography.Text>No filters applied — all users will be exported.</Typography.Text>
          )}
          <Typography.Text type="secondary">Change filters using the Filters button before exporting.</Typography.Text>
        </Space>
      </Modal>
    </>
  );
};

const ProfileCard = styled(Card)`
  height: unset;
`;

export default UsersPage;
